import { TextButton } from '../../widgets/buttons';
import { CopyInput } from '../../widgets/input';
import { FormControl } from '../../widgets/formControl';
import { QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { removeNotification, showNotification } from '../../widgets/notificationToaster';
import React, { FC, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import IfCanAccess from 'src/components/widgets/ifCanAccess/ifCanAccess';
import { T } from 'src/shared/helpers';
import { apiKeysMutation, apiKeysMutation$data } from './__generated__/apiKeysMutation.graphql';
import { apiKeysQuery } from './__generated__/apiKeysQuery.graphql';
import { SectionActionsFooter } from './myAccountComponents';
import Stack from '../../widgets/stack/stack';

interface Props {
    salesChannelID: string;
}

const ApiKeys: FC<Props> = ({ salesChannelID }) => {
    const { showDialog, closeDialog } = useDialogManager();

    const query = useLazyLoadQuery<apiKeysQuery>(
        graphql`
            query apiKeysQuery($id: ID!) {
                apiKeyBySalesChannelID(id: $id) {
                    clientID
                    secretKey
                }
            }
        `,
        { id: salesChannelID },
        { fetchPolicy: 'network-only' }
    );

    const [generateNewKey] = useMutation<apiKeysMutation>(graphql`
        mutation apiKeysMutation($id: ID!) {
            generateNewApiKey(id: $id) {
                clientID
                secretKey
            }
        }
    `);

    const [clientID, setClientID] = useState(query.apiKeyBySalesChannelID.clientID);
    const [apiKey, setApiKey] = useState(query.apiKeyBySalesChannelID.secretKey);

    function handleButtonClick(): void {
        showDialog(
            'generate-key',
            <QuestionDialog
                message={
                    <span>
                        {T._(
                            'Generating new API Keys will invalidate your current one: every application that uses it will be disconnected from Zakeke APIs. ',
                            'Admin'
                        )}
                        <br />
                        {T._('Are you sure you want to Proceed?', 'Admin')}
                    </span>
                }
                onYesClick={async () => {
                    const savingNotification = showNotification({
                        type: 'default',
                        message: T._('Generating new keys...', 'Admin')
                    });
                    try {
                        const result = await new Promise<apiKeysMutation$data>((resolve, reject) => {
                            generateNewKey({
                                onCompleted: resolve,
                                onError: reject,
                                variables: { id: salesChannelID }
                            });
                        });

                        const outputClientID = result.generateNewApiKey?.clientID;
                        const outputSecret = result.generateNewApiKey?.secretKey;

                        if (!outputSecret) {
                            showNotification({
                                type: 'danger',
                                message: T._('An error occurred while generating the Key.', 'Admin'),
                                dismiss: {
                                    duration: 5000
                                }
                            });
                        } else {
                            showNotification({
                                type: 'success',
                                message: T._('Keys were generated successfully.', 'Admin'),
                                dismiss: {
                                    duration: 5000
                                }
                            });

                            setClientID(outputClientID);
                            setApiKey(outputSecret);
                        }
                    } catch (ex) {
                        showNotification({
                            type: 'danger',
                            message: T._('An error occurred while generating keys.', 'Admin'),
                            dismiss: {
                                duration: 5000
                            }
                        });
                    } finally {
                        removeNotification(savingNotification);
                        closeDialog('generate-key');
                    }
                }}
                onNoClick={() => closeDialog('generate-key')}
            />
        );
    }

    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormControl label={T._('Client ID', 'Admin')}>
                    <CopyInput value={clientID} />
                </FormControl>
                <FormControl label={T._('Secret Key', 'Admin')}>
                    <CopyInput value={apiKey ?? ''} />
                </FormControl>
            </div>
            <SectionActionsFooter>
                <TextButton onClick={() => handleButtonClick()}>{T._('Generate new key', 'Admin')}</TextButton>
            </SectionActionsFooter>
        </>
    );
};

export default ({ salesChannelID }: { salesChannelID: string }) => (
    <IfCanAccess permission='OWNER'>
        <ApiKeys salesChannelID={salesChannelID} />
    </IfCanAccess>
);
